import { useCallback, useEffect, useState } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import type { Screen } from "./app/screens";
import { cloudAvailable } from "./cloud/api";
import { DetailCard } from "./components/DetailCard";
import type { Mode } from "./content/modes";
import { ninjaById, ninjaOfRank, type NinjaId } from "./content/ninjas";
import { RANKS } from "./content/ranks";
import { availableModes } from "./game/modes";
import { practiceSource } from "./game/practice";
import type { RoundResult } from "./game/results";
import { mathRandomRng, seededRng } from "./game/rng";
import { fighterFor, isKnown, nextToRecruit } from "./game/roster";
import { frameFor, goldPossible, setProgress } from "./game/sets";
import { cardStripes } from "./game/stripes";
import { newSeed, nextTrial, trialLock, trialSource } from "./game/trials";
import { battleSeconds } from "./game/battle";
import { fitFor } from "./game/items";
import { localChildren, useStore } from "./hooks/useStore";
import { liveChildren, type Account } from "./save/store";
import { Battle } from "./screens/Battle";
import { BattleResult } from "./screens/BattleResult";
import { Home } from "./screens/Home";
import { ImportLocal } from "./screens/ImportLocal";
import { Ladder } from "./screens/Ladder";
import { Parents } from "./screens/Parents";
import { Placement } from "./screens/Placement";
import { Play } from "./screens/Play";
import { Privacy } from "./screens/Privacy";
import { Result } from "./screens/Result";
import { Roster } from "./screens/Roster";
import { Shop } from "./screens/Shop";
import { BattleStaging, PracticeStaging, TrialStaging } from "./screens/Staging";
import { SignIn } from "./screens/SignIn";
import { WhosPlaying } from "./screens/WhosPlaying";
import { unlockSpeech } from "./speech/speech";

export default function App() {
  const { store, child, view: save, status, actions } = useStore();
  const kids = liveChildren(store);
  const [screen, setScreen] = useState<Screen>(() =>
    kids.length > 1 ? { name: "whos", canGoBack: false } : { name: "home" },
  );
  const [detail, setDetail] = useState<{ rankIndex: number; mode: Mode | undefined } | null>(null);
  const [roundId, setRoundId] = useState(0);
  const [parentsUnlocked, setParentsUnlocked] = useState(false);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  // iPadOS only allows speech after a tap: unlock it on the first one.
  useEffect(() => {
    const unlock = () => unlockSpeech();
    document.addEventListener("pointerdown", unlock, { once: true });
    return () => document.removeEventListener("pointerdown", unlock);
  }, []);

  const modes = availableModes(save.settings.ops);
  const mode = modes.includes(save.mode) ? save.mode : modes[0]!;

  const show = useCallback(
    (next: Screen) => {
      window.scrollTo(0, 0);
      // Leaving the grown-up screens locks Parents again.
      if (!["parents", "signin", "import", "privacy", "placement"].includes(next.name))
        setParentsUnlocked(false);
      // A card popup doesn't outlive the screen it was opened on.
      setDetail(null);
      setScreen(next);
    },
    [setParentsUnlocked, setDetail, setScreen],
  );
  const goHome = useCallback(() => show({ name: "home" }), [show]);

  const startTrial = useCallback(
    (m: Mode) => {
      const spec = nextTrial(m, save.rank[m]);
      if (!spec) return;
      // Strands climb together: a locked strand's door explains why.
      if (trialLock(save.rank, save.settings.ops, m)) return show({ name: "trial-staging" });
      setRoundId((n) => n + 1);
      show({ name: "trial", roundId: roundId + 1, spec, seed: newSeed() });
    },
    [save.rank, save.settings.ops, roundId, show],
  );
  const startPractice = useCallback(
    (m: Mode) => {
      setRoundId((n) => n + 1);
      show({ name: "practice", roundId: roundId + 1, mode: m });
    },
    [roundId, show],
  );
  /** A battle against `enemy`, or the next ninja to recruit (Legend once the roster is full). */
  const startBattle = useCallback(
    (m: Mode, enemy?: NinjaId) => {
      const foe = enemy ?? nextToRecruit(save.roster)?.id ?? ninjaOfRank(RANKS.length - 1).id;
      setRoundId((n) => n + 1);
      show({ name: "battle", roundId: roundId + 1, mode: m, enemy: foe, seed: newSeed() });
    },
    [save.roster, roundId, show],
  );

  const trialFinished = useCallback(
    (round: RoundResult) => {
      if (screen.name !== "trial") return;
      show({ name: "result", outcome: actions.recordTrial(screen.spec, round, screen.seed) });
    },
    [screen, actions, show],
  );
  const practiceFinished = useCallback(
    (round: RoundResult) => {
      if (screen.name !== "practice") return;
      if (round.tried === 0) return goHome();
      show({ name: "result", outcome: actions.recordPractice(screen.mode, round) });
    },
    [screen, actions, show, goHome],
  );
  const fighter = fighterFor(save.roster, save.settings.fighter);
  const kit = fitFor(fighter, save.settings.loadout, save.items);
  const battleFinished = useCallback<React.ComponentProps<typeof Battle>["onFinished"]>(
    (result) => {
      if (screen.name !== "battle") return;
      const enemy = ninjaById(screen.enemy)!;
      const outcome = actions.recordBattle(
        {
          mode: screen.mode,
          enemy,
          fighter,
          seconds: battleSeconds(fighter, kit),
          seed: screen.seed,
        },
        result,
      );
      show({ name: "battle-result", outcome });
    },
    [screen, actions, fighter, kit, show],
  );
  const placed = useCallback(
    (ranks: Partial<Record<Mode, number>>) => {
      actions.placed(ranks);
      goHome();
    },
    [actions, goHome],
  );
  const signedIn = useCallback(
    (account: Account) => {
      actions.signedIn(account);
      show({ name: "import" });
    },
    [actions, show],
  );
  const closeDetail = useCallback(() => setDetail(null), [setDetail]);

  const locals = localChildren(store);
  const family = kids.filter((c) => c.synced);
  const familyLoading = status.syncing || !store.lastSync;
  const openCard = useCallback(
    (rankIndex: number, m?: Mode) => setDetail({ rankIndex, mode: m }),
    [setDetail],
  );

  return (
    <>
      {screen.name === "home" && (
        <>
          <Home
            save={save}
            mode={mode}
            onSelectMode={actions.setMode}
            onTrial={() => show({ name: "trial-staging" })}
            onBattle={() => show({ name: "battle-staging" })}
            onPractice={() => show({ name: "practice-staging" })}
            onLadder={() => show({ name: "ladder" })}
            onRoster={() => show({ name: "roster" })}
            onShop={() => show({ name: "shop" })}
            onParents={() => show({ name: "parents" })}
            onOpenCard={openCard}
            onPlacement={() => show({ name: "placement", adjustable: false })}
            onSkipPlacement={actions.skipPlacement}
            onSwitchPlayer={
              kids.length > 1 ? () => show({ name: "whos", canGoBack: true }) : undefined
            }
            promoted={child.celebrate}
            onDismissPromoted={actions.dismissPromoted}
          />
          {needRefresh && (
            <button
              type="button"
              className="ghost update-note"
              onClick={() => void updateServiceWorker(true)}
            >
              An update is ready. Tap to restart.
            </button>
          )}
        </>
      )}
      {screen.name === "whos" && (
        <WhosPlaying
          kids={kids}
          onChoose={(id) => {
            actions.selectChild(id);
            goHome();
          }}
          onBack={screen.canGoBack ? goHome : undefined}
        />
      )}
      {screen.name === "trial-staging" && (
        <TrialStaging
          save={save}
          mode={mode}
          onSelectMode={actions.setMode}
          onStart={() => startTrial(mode)}
          onBack={goHome}
          onOpenCard={openCard}
        />
      )}
      {screen.name === "practice-staging" && (
        <PracticeStaging
          save={save}
          mode={mode}
          onSelectMode={actions.setMode}
          onStart={() => startPractice(mode)}
          onBack={goHome}
        />
      )}
      {screen.name === "battle-staging" && (
        <BattleStaging
          save={save}
          mode={mode}
          enemy={
            (screen.enemy && ninjaById(screen.enemy)) ??
            nextToRecruit(save.roster) ??
            ninjaOfRank(RANKS.length - 1)
          }
          fighter={fighter}
          kit={kit}
          onSelectMode={actions.setMode}
          onStart={() => startBattle(mode, screen.enemy)}
          onRoster={() => show({ name: "roster" })}
          onBack={goHome}
        />
      )}
      {screen.name === "trial" && (
        <Play
          key={screen.roundId}
          context={{
            next: trialSource(screen.spec, save.settings.ops, seededRng(screen.seed)),
            seconds: screen.spec.seconds,
          }}
          countdown
          label="Trial"
          end="stop"
          target={screen.spec}
          bar={save.settings.trialBar}
          rankColour={RANKS[screen.spec.rankIndex]?.colour}
          audio={save.settings.audio}
          onFinished={trialFinished}
          onStopped={goHome}
        />
      )}
      {screen.name === "practice" && (
        <Play
          key={screen.roundId}
          context={{
            next: practiceSource({
              mode: screen.mode,
              rank: save.rank[screen.mode],
              ops: save.settings.ops,
              practice: save.settings.practice,
              mastery: save.mastery,
              missed: save.missed,
              rng: mathRandomRng,
            }),
            seconds: save.settings.practice.minutes ? save.settings.practice.minutes * 60 : null,
          }}
          countdown
          label="Practice"
          end="done"
          rankColour={RANKS[save.rank[screen.mode]]?.colour}
          audio={save.settings.audio}
          onFinished={practiceFinished}
          onStopped={goHome}
        />
      )}
      {screen.name === "battle" && (
        <Battle
          key={screen.roundId}
          save={save}
          mode={screen.mode}
          enemy={ninjaById(screen.enemy)!}
          fighter={fighter}
          kit={kit}
          known={isKnown(save.rank, save.roster, ninjaById(screen.enemy)!)}
          seed={screen.seed}
          audio={save.settings.audio}
          onFinished={battleFinished}
          onStopped={goHome}
        />
      )}
      {screen.name === "result" && (
        <Result
          outcome={screen.outcome}
          rank={save.rank[screen.outcome.mode]}
          stripes={cardStripes(save.stripes, screen.outcome.mode, save.rank[screen.outcome.mode])}
          frame={frameFor(save.sets, save.rank[screen.outcome.mode])}
          dots={
            screen.outcome.kind === "trial" && goldPossible(save.settings.ops)
              ? setProgress(save.rank, save.settings.ops, save.rank[screen.outcome.mode])
              : undefined
          }
          onTryAgain={() => startTrial(screen.outcome.mode)}
          onPractice={() => startPractice(screen.outcome.mode)}
          onHome={goHome}
          onOpenCard={openCard}
        />
      )}
      {screen.name === "battle-result" && (
        <BattleResult
          outcome={screen.outcome}
          save={save}
          nextName={(() => {
            const next = nextToRecruit(save.roster);
            return next
              ? isKnown(save.rank, save.roster, next)
                ? next.name
                : "a mystery ninja"
              : null;
          })()}
          onAgain={() => startBattle(screen.outcome.mode, screen.outcome.enemy.id)}
          onNext={() => startBattle(screen.outcome.mode)}
          onRoster={() => show({ name: "roster" })}
          onShop={() => show({ name: "shop" })}
          onHome={goHome}
          onOpenCard={openCard}
        />
      )}
      {screen.name === "placement" && (
        <Placement
          key={child.id}
          save={save}
          adjustable={screen.adjustable}
          onDone={placed}
          onCancel={screen.adjustable ? () => show({ name: "parents" }) : goHome}
        />
      )}
      {screen.name === "ladder" && (
        <Ladder save={save} mode={mode} onOpenCard={openCard} onBack={goHome} />
      )}
      {screen.name === "roster" && <Roster save={save} onOpenCard={openCard} onBack={goHome} />}
      {screen.name === "shop" && (
        <Shop
          save={save}
          onBuy={actions.buy}
          onFit={() => show({ name: "roster" })}
          onBack={goHome}
        />
      )}
      {screen.name === "parents" && (
        <Parents
          store={store}
          status={status}
          actions={actions}
          cloudAvailable={cloudAvailable}
          onPlacement={(id) => {
            actions.selectChild(id);
            show({ name: "placement", adjustable: true });
          }}
          onSignIn={() => show({ name: "signin" })}
          onPrivacy={() => show({ name: "privacy", back: "parents" })}
          onBack={goHome}
          unlocked={parentsUnlocked}
          onUnlock={() => setParentsUnlocked(true)}
        />
      )}
      {screen.name === "signin" && (
        <SignIn
          onSignedIn={signedIn}
          onPrivacy={() => show({ name: "privacy", back: "signin" })}
          onBack={() => show({ name: "parents" })}
        />
      )}
      {screen.name === "import" && (
        <ImportLocal
          locals={locals}
          family={family}
          loading={familyLoading}
          onDone={(choices) => {
            actions.importLocal(choices);
            show(
              liveChildren(store).filter((c) => c.synced).length > 1
                ? { name: "whos", canGoBack: false }
                : { name: "parents" },
            );
          }}
        />
      )}
      {screen.name === "privacy" && <Privacy onBack={() => show({ name: screen.back })} />}
      <DetailCard
        rankIndex={detail?.rankIndex ?? null}
        save={save}
        mode={
          detail?.mode ??
          (screen.name === "result" || screen.name === "battle-result" ? screen.outcome.mode : mode)
        }
        onClose={closeDetail}
        onWear={actions.wearLook}
        onFight={actions.setFighter}
        onChallenge={(id) => show({ name: "battle-staging", enemy: id as NinjaId })}
        onFit={actions.fitItem}
        onShop={() => show({ name: "shop" })}
      />
    </>
  );
}
